import React from 'react';

import Table from './Table';
import TableHead from './TableHead';
import TableRow from './TableRow';
import TableCell from './TableCell';
import TableBody from './TableBody';
import TableFoot from './TableFoot';

import { ReportTableType } from '../../types/ReportTableType';
import { ReportTableColumnType } from '../../types/ReportTableColumnType';

import { REPORTS_TABLES_COLUMNS } from '../../constants/ReportsTablesColumns';

interface LocationsReportTableProps {
    reportType: ReportTableType;
    headerTableData?: ReportTableColumnType[];
    bodyTableData: Record<string, any>[];
    footerTableData?: Record<string, any>[];
}

const LocationsReportTable: React.FC<LocationsReportTableProps> = (props) => {
    const { reportType, headerTableData, bodyTableData, footerTableData } = props;

    const [reportTableColumnsBaseHeader, setReportTableColumnsBaseHeader] = React.useState<any[]>([]);
    const [lotsLocReportTotalSum, setLotsLocReportTotalSum] = React.useState<number>(0);

    React.useEffect(() => {
        // Inject dynamic header columns to the base columns
        if (headerTableData && headerTableData.length > 0) {
            const tmpBaseCols = REPORTS_TABLES_COLUMNS[reportType].slice();
            tmpBaseCols.splice(2, 0, ...headerTableData);
            setReportTableColumnsBaseHeader(tmpBaseCols);
        }
    }, [headerTableData, REPORTS_TABLES_COLUMNS]);

    React.useEffect(() => {
        // Calculate total sum of the report for table footer
        if (bodyTableData && bodyTableData.length > 0) {
            let reportTotalSum = 0;
            bodyTableData.forEach((lot) => {
                lot.locations.forEach((location: Record<string, any>) => {
                    reportTotalSum += location.totalExclVat;
                });
            });
            setLotsLocReportTotalSum(reportTotalSum);
        }
    }, [bodyTableData]);

    return (
        <Table additionalClasses="is-striped is-hoverable is-fullwidth">
            <TableHead>
                <TableRow>
                    {reportTableColumnsBaseHeader.map((column) => (
                        <TableCell
                            key={column.key}
                            type="head"
                            draggable={false}
                            additionalClasses={column.additionalClasses}
                        >
                            <div className="th-wrap is-relative">{column.title}</div>
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>

            {bodyTableData && bodyTableData.length > 0 ? (
                <React.Fragment>
                    <TableBody>
                        {bodyTableData.map((row) => (
                            <TableRow key={row.lotId}>
                                <TableCell type="body" draggable={false}>
                                    {row.lotCode}
                                </TableCell>
                                <TableCell type="body" draggable={false}>
                                    {row.lotTitle}
                                </TableCell>

                                {row.locations && row.locations.length > 0 && (
                                    <React.Fragment>
                                        {row.locations.map((location: Record<string, any>, index: number) => (
                                            <TableCell
                                                key={location.locationId}
                                                type="body"
                                                draggable={false}
                                                additionalClasses="has-text-right"
                                            >
                                                {location.totalExclVat.toLocaleString('fr-FR', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}{' '}
                                                €
                                            </TableCell>
                                        ))}
                                    </React.Fragment>
                                )}

                                <TableCell type="body" draggable={false} additionalClasses="has-text-right">
                                    {row.lotTotalExclVat.toLocaleString('fr-FR', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}{' '}
                                    €
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>

                    <TableFoot>
                        <TableRow additionalClasses="table-footer has-background-light">
                            <TableCell type="foot" additionalClasses="is-hidden-mobile">
                                <div className="th-wrap is-numeric">{null}</div>
                            </TableCell>
                            <TableCell type="foot" additionalClasses="is-hidden-mobile">
                                <div className="th-wrap">Total</div>
                            </TableCell>

                            {footerTableData.map((footerLocCol) => (
                                <TableCell
                                    key={footerLocCol.locationId}
                                    type="foot"
                                    additionalClasses="is-hidden-mobile has-text-right"
                                >
                                    <div className="th-wrap is-numeric">
                                        {footerLocCol.lotsLocTotalExclVat.toLocaleString('fr-FR', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        })}{' '}
                                        €
                                    </div>
                                </TableCell>
                            ))}

                            <TableCell type="foot" additionalClasses="has-text-right" draggable={false}>
                                {lotsLocReportTotalSum?.toLocaleString('fr-FR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}{' '}
                                €
                            </TableCell>
                        </TableRow>
                    </TableFoot>
                </React.Fragment>
            ) : null}
        </Table>
    );
};

export default LocationsReportTable;
